import { useEffect, useState } from "react";
import { Modal } from "@/common/components/modal";
import Form from "@/common/components/form/Form";
import { INVENTORY_URL } from "@/common/constants";
import { KeyValueObj } from "@/common/types";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { buildEditBatchForm } from "./data";

interface IEditBatchModalProps {
  isOpen: boolean;
  close: () => void;
  batch: KeyValueObj | null;
  onUpdated: (updated: KeyValueObj) => void;
}

// Relocated from the old flat inventory list's row-menu "Edit" action (which
// no longer makes sense once stage-1 rows aggregate multiple batches) — this
// is the same PUT /inventories/:id capability, now living where a single
// batch is actually in view. Only the fields InventoryConcern#update_params
// permits are editable; batch identity/dates/cost are immutable.
export function EditBatchModal({ isOpen, close, batch, onUpdated }: IEditBatchModalProps) {
  const [packageTypeOptions, setPackageTypeOptions] = useState<
    { id: string; title: string }[]
  >([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const apiCall = useApiCallHandling();

  useEffect(() => {
    if (!isOpen || !batch?.id) return;
    apiCall(
      `${INVENTORY_URL}/${batch.id}/selling-package-types`,
      "GET",
      {},
      (data: KeyValueObj[]) => {
        setPackageTypeOptions(
          (data || []).map((layer: KeyValueObj) => ({
            id: layer.packaging_type,
            title: layer.packaging_type,
          })),
        );
      },
      () => {},
      () => {},
      { dontNotifyOnSucess: true, dontNotifyonFail: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, batch?.id]);

  function onSubmit(values: KeyValueObj) {
    if (!batch?.id) return;
    setSubmitLoading(true);
    apiCall(
      `${INVENTORY_URL}/${batch.id}`,
      "PUT",
      values,
      (data: KeyValueObj) => {
        onUpdated(data);
        close();
      },
      () => {},
      () => setSubmitLoading(false),
    );
  }

  const formInputs = buildEditBatchForm(packageTypeOptions).map((field) => ({
    ...field,
    initialValue: batch?.[field.name],
  }));

  return (
    <Modal isOpen={isOpen} close={close} title="Edit Batch">
      <div className="w-[30rem] max-w-[90vw] px-4 py-2">
        {batch && (
          <Form
            loadingState={submitLoading}
            submitLabel="Save Changes"
            formInputs={formInputs}
            processInputs={onSubmit}
            spinnerComponent="spinner"
            login={false}
            biControl={false}
          />
        )}
      </div>
    </Modal>
  );
}
